import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Send, Trash2, Bot, User, Sparkles, AlertCircle, ChevronDown } from 'lucide-react';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Panel = styled.div`
  width: ${p => p.$open ? '380px' : '0'};
  min-width: ${p => p.$open ? '380px' : '0'};
  height: 100vh;
  position: fixed;
  right: ${p => p.$rightOffset || '0'};
  top: 0;
  background: var(--bg);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  z-index: 1050;
  transition: width .25s ease, min-width .25s ease;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--bg);
`;

const PanelTitle = styled.div`
  font-size: 13px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px; flex: 1;
`;

const GeminiLogo = styled.div`
  width: 28px; height: 28px; border-radius: 8px;
  background: linear-gradient(135deg, #4285f4, #a855f7, #ec4899);
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
`;

const HeaderBtn = styled.button`
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: transparent;
  color: var(--text-muted);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s;
  &:hover { background: var(--surface); color: var(--primary); }
`;

const ModelSelectorWrap = styled.div`
  position: relative;
  display: flex; align-items: center;
`;

const ModelBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 6px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-secondary);
  font-size: 10px; font-weight: 600; cursor: pointer;
  transition: all .15s; white-space: nowrap;
  max-width: 140px; overflow: hidden; text-overflow: ellipsis;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const ModelDropdown = styled.div`
  position: absolute; top: 100%; left: 0; margin-top: 4px;
  min-width: 220px; max-height: 320px; overflow-y: auto;
  background: var(--bg); border: 1px solid var(--border);
  border-radius: 10px; box-shadow: 0 8px 32px rgba(0,0,0,.35);
  z-index: 1100; padding: 6px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ModelProviderLabel = styled.div`
  font-size: 9px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.8px;
  padding: 6px 8px 3px; margin-top: 2px;
`;

const ModelOption = styled.button`
  width: 100%; display: flex; align-items: center; gap: 8px;
  padding: 7px 10px; border-radius: 7px; border: none;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-primary)'};
  font-size: 11px; font-weight: 500; cursor: pointer;
  text-align: left; transition: all .12s;
  &:hover { background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'}; }
`;

const ModelDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$color || 'var(--primary)'};
`;

const ModelDesc = styled.span`
  font-size: 10px; color: ${p => p.$active ? 'rgba(255,255,255,.7)' : 'var(--text-muted)'};
  margin-left: auto; white-space: nowrap;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const WelcomeBlock = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 12px; padding: 40px 20px;
  text-align: center; flex: 1;
`;

const WelcomeIcon = styled.div`
  width: 56px; height: 56px; border-radius: 16px;
  background: linear-gradient(135deg, #4285f4, #a855f7, #ec4899);
  display: flex; align-items: center; justify-content: center;
  color: #fff;
`;

const WelcomeTitle = styled.div`
  font-size: 16px; font-weight: 700; color: var(--text-primary);
`;

const WelcomeSubtitle = styled.div`
  font-size: 12px; color: var(--text-muted); line-height: 1.5;
  max-width: 280px;
`;

const SuggestionGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  width: 100%; max-width: 320px; margin-top: 8px;
`;

const SuggestionBtn = styled.button`
  padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-primary);
  font-size: 11px; font-weight: 500; cursor: pointer;
  text-align: left; line-height: 1.4; transition: all .15s;
  &:hover { border-color: var(--primary); background: var(--surface-hover); color: var(--primary); }
`;

const MessageBubble = styled.div`
  max-width: 90%;
  align-self: ${p => p.$isUser ? 'flex-end' : 'flex-start'};
  display: flex; gap: 8px;
  flex-direction: ${p => p.$isUser ? 'row-reverse' : 'row'};
  align-items: flex-start;
`;

const Avatar = styled.div`
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$isUser ? 'var(--primary)' : 'linear-gradient(135deg, #4285f4, #a855f7)'};
  color: #fff;
`;

const BubbleContent = styled.div`
  padding: 10px 14px;
  border-radius: ${p => p.$isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  background: ${p => p.$isUser ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$isUser ? '#fff' : 'var(--text-primary)'};
  font-size: 12px; line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;

  code {
    background: ${p => p.$isUser ? 'rgba(255,255,255,.15)' : 'rgba(165,145,239,.1)'};
    padding: 1px 5px; border-radius: 4px; font-size: 11px;
    font-family: 'Fira Code', monospace;
  }

  pre {
    background: ${p => p.$isUser ? 'rgba(0,0,0,.2)' : 'rgba(0,0,0,.05)'};
    padding: 10px; border-radius: 8px; overflow-x: auto;
    margin: 8px 0;
    code { background: none; padding: 0; }
  }

  strong { font-weight: 700; }

  ul, ol { padding-left: 18px; margin: 4px 0; }
  li { margin: 2px 0; }

  h1, h2, h3 { margin: 8px 0 4px; font-weight: 700; }
  h1 { font-size: 15px; }
  h2 { font-size: 14px; }
  h3 { font-size: 13px; }
`;

const ErrorBubble = styled.div`
  padding: 10px 14px; border-radius: 10px;
  background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.2);
  color: var(--error); font-size: 11px;
  display: flex; align-items: center; gap: 8px;
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: .3; transform: scale(.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const TypingIndicator = styled.div`
  display: flex; gap: 4px; align-items: center; padding: 10px 14px;
  align-self: flex-start; background: var(--surface);
  border-radius: 14px 14px 14px 4px;
`;

const Dot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--text-muted);
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${p => p.$delay || '0s'};
`;

const InputArea = styled.div`
  padding: 12px 16px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--bg);
`;

const InputRow = styled.form`
  display: flex; align-items: flex-end; gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 6px 8px 6px 14px;
  transition: border-color .2s;
  &:focus-within { border-color: var(--primary); }
`;

const TextArea = styled.textarea`
  flex: 1; border: none; background: transparent;
  color: var(--text-primary); font-size: 13px;
  resize: none; outline: none; min-height: 20px; max-height: 120px;
  font-family: inherit; line-height: 1.5;
  padding: 4px 0;
  &::placeholder { color: var(--text-muted); }
`;

const SendBtn = styled.button`
  width: 32px; height: 32px; border-radius: 10px; border: none;
  background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  display: flex; align-items: center; justify-content: center;
  cursor: ${p => p.$active ? 'pointer' : 'default'};
  transition: all .15s; flex-shrink: 0;
  &:hover { ${p => p.$active && 'transform: scale(1.05);'} }
`;

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

const SUGGESTIONS = [
  'Comment fonctionne le screen mirroring ?',
  'Liste les tables PostgreSQL',
  'Comment lancer un workflow ?',
  'Explique le système de workstations'
];

function formatMessage(text) {
  if (!text) return '';
  return text
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^- (.+)$/gm, '• $1')
    .replace(/\n/g, '<br />');
}

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const PROVIDER_COLORS = { gemini: '#8b5cf6', openai: '#10a37f', claude: '#d97706' };
const PROVIDER_LABELS = { gemini: 'Google Gemini', openai: 'OpenAI', claude: 'Anthropic' };

const GeminiChatPanel = ({ isOpen, onClose, rightOffset }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId] = useState(() => 'chat-' + Date.now());
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem('qlickr_ai_model') || 'gemini-2.5-flash-lite');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Load available models
  useEffect(() => {
    if (isOpen) {
      fetch('/api/gemini-chat/models').then(r => r.json()).then(data => {
        setAvailableModels(data.models || []);
      }).catch(() => {});
    }
  }, [isOpen]);

  // Close model dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);

    try {
      const res = await fetch('/api/gemini-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, conversationId, model: selectedModel })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');

      setMessages(prev => [...prev, { role: 'model', text: data.reply }]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    setMessages([]);
    setError(null);
    try {
      await fetch('/api/gemini-chat/clear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId })
      });
    } catch (e) { /* silent */ }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Panel $open={isOpen} $rightOffset={rightOffset}>
      <PanelHeader>
        <GeminiLogo><Sparkles size={15} /></GeminiLogo>
        <PanelTitle>QLICKR AI</PanelTitle>
        <ModelSelectorWrap ref={modelDropdownRef}>
          <ModelBtn onClick={() => setModelDropdownOpen(p => !p)}>
            {availableModels.find(m => m.id === selectedModel)?.label || selectedModel}
            <ChevronDown size={10} />
          </ModelBtn>
          {modelDropdownOpen && (
            <ModelDropdown>
              {['gemini', 'openai', 'claude'].map(provider => {
                const providerModels = availableModels.filter(m => m.provider === provider);
                if (providerModels.length === 0) return null;
                return (
                  <React.Fragment key={provider}>
                    <ModelProviderLabel>{PROVIDER_LABELS[provider]}</ModelProviderLabel>
                    {providerModels.map(m => (
                      <ModelOption
                        key={m.id}
                        $active={selectedModel === m.id}
                        onClick={() => {
                          setSelectedModel(m.id);
                          localStorage.setItem('qlickr_ai_model', m.id);
                          setModelDropdownOpen(false);
                        }}
                      >
                        <ModelDot $color={PROVIDER_COLORS[provider]} />
                        {m.label}
                        <ModelDesc $active={selectedModel === m.id}>{m.desc}</ModelDesc>
                      </ModelOption>
                    ))}
                  </React.Fragment>
                );
              })}
            </ModelDropdown>
          )}
        </ModelSelectorWrap>
        <HeaderBtn onClick={clearChat} title="Effacer la conversation">
          <Trash2 size={13} />
        </HeaderBtn>
        <HeaderBtn onClick={onClose} title="Fermer">
          <X size={14} />
        </HeaderBtn>
      </PanelHeader>

      <MessagesArea>
        {messages.length === 0 && !loading ? (
          <WelcomeBlock>
            <WelcomeIcon><Sparkles size={26} /></WelcomeIcon>
            <WelcomeTitle>QLICKR AI</WelcomeTitle>
            <WelcomeSubtitle>
              Assistant IA propulsé par Gemini. Pose-moi des questions sur le projet, les fonctionnalités, ou l'architecture.
            </WelcomeSubtitle>
            <SuggestionGrid>
              {SUGGESTIONS.map((s, i) => (
                <SuggestionBtn key={i} onClick={() => sendMessage(s)}>{s}</SuggestionBtn>
              ))}
            </SuggestionGrid>
          </WelcomeBlock>
        ) : (
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} $isUser={msg.role === 'user'}>
                <Avatar $isUser={msg.role === 'user'}>
                  {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                </Avatar>
                <BubbleContent
                  $isUser={msg.role === 'user'}
                  dangerouslySetInnerHTML={{ __html: msg.role === 'user' ? msg.text : formatMessage(msg.text) }}
                />
              </MessageBubble>
            ))}
            {loading && (
              <TypingIndicator>
                <Dot $delay="0s" />
                <Dot $delay="0.2s" />
                <Dot $delay="0.4s" />
              </TypingIndicator>
            )}
            {error && (
              <ErrorBubble>
                <AlertCircle size={14} />
                {error}
              </ErrorBubble>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </MessagesArea>

      <InputArea>
        <InputRow onSubmit={handleSubmit}>
          <TextArea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pose une question sur le projet..."
            rows={1}
            disabled={loading}
          />
          <SendBtn $active={input.trim().length > 0 && !loading} onClick={handleSubmit} type="submit">
            <Send size={14} />
          </SendBtn>
        </InputRow>
      </InputArea>
    </Panel>
  );
};

export default GeminiChatPanel;
